/**
 * ChimeraSkillEffect — Conjuration Bind path Fork 2 active: dual-adapted companion.
 *
 * Replaces Empower. Spawns a single companion familiar with two adapted
 * behaviors drawn from randomly-selected non-Conjuration trees (fresh roll
 * per summon). 110 HP, 17 damage. Tactical AI mode inherited from Empower.
 *
 * Pre-Synthesis: the integration layer alternates between the two behaviors
 * on successive attacks (A, B, A, B...).
 * Post-Synthesis (chimera_upgrade_1): both fire simultaneously on each hit.
 *
 * Upgrade hooks:
 *   - Bind-path inherited (resolved via 'bind' scope):
 *     - Fortify: hpBonus additive (+25), interceptEnabled boolean
 *     - Resonant: damageBonus additive (+5)
 *   - Chimera-specific (resolved via 'chimera' scope):
 *     - Synthesis: dualBehaviorSimultaneous boolean
 *     - Apex: hpBonus additive (+30), damageBonus additive (+4),
 *       lowHpOverdriveMultiplier (1.5×), lowHpOverdriveDurationMs (5000),
 *       lowHpThreshold (0.3). Overdrive state machine: activates below
 *       threshold, runs for duration, enters cooldown until healed above.
 *
 * Long cooldown tier (2000ms). Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see conjuration-spec.md § Chimera
 */

import type { SkillEffect } from '../../SkillEffect.js';
import {
  type CompanionSummonConfig,
  type AdaptedBehaviorConfig,
  resolveAdaptedBehavior,
} from './BindSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface ChimeraDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
    getMinEffect(skillId: string, key: string): number | undefined;
    getMaxEffect(skillId: string, key: string): number | undefined;
  };
  readonly player: {
    getPosition(): { x: number; y: number };
  };
  readonly targeting: {
    /** Returns true if the companion should fail to manifest (XP-based freshness check). */
    shouldFizzle(): boolean;
  };
  readonly summons: {
    spawn(config: CompanionSummonConfig, x: number, y: number): string;
  };
  readonly progression: {
    /**
     * Returns N randomly-selected non-Conjuration tree IDs from the player's
     * equipped trees. Fresh roll per call. Returns fewer than N if the player
     * has fewer equipped non-Conjuration trees.
     */
    getRandomNonConjurationTrees(count: number): string[];
  };
}

// ---------------------------------------------------------------------------
// Skill tuning
// ---------------------------------------------------------------------------

export interface ChimeraSkillConfig {
  readonly cooldownMs: number;
  /** Base companion HP before upgrade bonuses. */
  readonly baseHp: number;
  /** Base companion damage per hit before upgrade bonuses. */
  readonly baseDamage: number;
  /** Base companion movement speed. */
  readonly baseSpeed: number;
  /** Max distance from player before returning (tiles). */
  readonly leashRadiusTiles: number;
  /** Max distance from familiar to target before engaging (tiles). */
  readonly attackRangeTiles: number;
  /** Delay between swings in ms. */
  readonly attackRecoveryMs: number;
  /** How much enemy aggro the familiar draws (0–1). */
  readonly baseAggroWeight: number;
  /** HP fraction (0–1) below which the familiar retreats to follow mode. */
  readonly retreatHpThreshold: number;
}

const DEFAULT_CONFIG: ChimeraSkillConfig = {
  cooldownMs: 2000,                // long cooldown tier
  baseHp: 110,                     // spec: 110 HP (up from Empower's 95)
  baseDamage: 17,                  // spec: 17 damage per hit (up from Empower's 14)
  baseSpeed: 1.0,                  // integration layer applies px/s
  leashRadiusTiles: 2,             // spec: stay within 2 tiles of player
  attackRangeTiles: 3,             // spec: attack enemies within 3 tiles
  attackRecoveryMs: 1000,          // spec: 1s recovery between swings
  baseAggroWeight: 0.3,            // moderate aggro draw
  retreatHpThreshold: 0.5,         // spec: retreat when HP < 50%
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class ChimeraSkillEffect implements SkillEffect {
  readonly skillId = 'chimera';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly failLabel = 'FIZZLE';

  private readonly deps: ChimeraDeps;
  private readonly config: ChimeraSkillConfig;

  private _cooldownRemaining = 0;
  private _familiarAlive = false;
  private _familiarId = '';
  private _fizzled = false;
  private _spawnsCount = 0;
  private _resolvedHp = 0;
  private _resolvedDamage = 0;
  private _interceptEnabled = false;
  private _interceptCooldownMs = 0;
  private _interceptMinHp = 0;
  private _dualBehaviors: readonly [AdaptedBehaviorConfig, AdaptedBehaviorConfig] = [
    { type: 'none' },
    { type: 'none' },
  ];
  private _dualBehaviorSimultaneous = false;
  private _overdriveMultiplier = 0;
  private _overdriveDurationMs = 0;
  private _overdriveHpThreshold = 0;
  private _overdriveActive = false;
  private _overdriveCooldown = false;
  private _overdriveTimerRemaining = 0;

  constructor(deps: ChimeraDeps, config: Partial<ChimeraSkillConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters (observable by scenarios and integration layer) ---

  get cooldown(): number { return this._cooldownRemaining; }
  get familiarAlive(): boolean { return this._familiarAlive; }
  get familiarId(): string { return this._familiarId; }
  get fizzled(): boolean { return this._fizzled; }
  get spawnsCount(): number { return this._spawnsCount; }
  get resolvedHp(): number { return this._resolvedHp; }
  get resolvedDamage(): number { return this._resolvedDamage; }
  get interceptEnabled(): boolean { return this._interceptEnabled; }
  get interceptCooldownMs(): number { return this._interceptCooldownMs; }
  get interceptMinHp(): number { return this._interceptMinHp; }
  get dualBehaviors(): readonly [AdaptedBehaviorConfig, AdaptedBehaviorConfig] {
    return this._dualBehaviors;
  }
  get dualBehaviorSimultaneous(): boolean { return this._dualBehaviorSimultaneous; }
  get overdriveMultiplier(): number { return this._overdriveMultiplier; }
  get overdriveDurationMs(): number { return this._overdriveDurationMs; }
  get overdriveHpThreshold(): number { return this._overdriveHpThreshold; }
  get overdriveActive(): boolean { return this._overdriveActive; }
  get overdriveCooldown(): boolean { return this._overdriveCooldown; }

  // --- Integration layer hooks ---

  /**
   * Called by the integration layer when the companion dies.
   * Wired to SummonSystem's 'died' event for archetype 'companion'.
   */
  onFamiliarDied(): void {
    this._familiarAlive = false;
    this._familiarId = '';
    this._overdriveActive = false;
    this._overdriveCooldown = false;
    this._overdriveTimerRemaining = 0;
  }

  /**
   * Called by the integration layer on each familiar HP update.
   * Drives the Apex overdrive state machine: if HP drops below threshold
   * and overdrive is neither active nor on cooldown, enter overdrive.
   * When the familiar heals above threshold, the cooldown flag clears —
   * allowing re-trigger on the next dip (intentional Devoted synergy).
   */
  onFamiliarHpChanged(currentHp: number, maxHp: number): void {
    if (this._overdriveMultiplier <= 0 || maxHp <= 0) return;

    const ratio = currentHp / maxHp;

    // Heal above threshold → clear cooldown (enables re-trigger)
    if (ratio >= this._overdriveHpThreshold) {
      this._overdriveCooldown = false;
      return;
    }

    // Below threshold: enter overdrive if not already active and not on cooldown
    if (!this._overdriveActive && !this._overdriveCooldown) {
      this._overdriveActive = true;
      this._overdriveTimerRemaining = this._overdriveDurationMs;
    }
  }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0 && !this._familiarAlive;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Freshness fizzle check — consumes cooldown but does not spawn
    this._fizzled = this.deps.targeting.shouldFizzle();
    if (this._fizzled) {
      this._cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    const pos = this.deps.player.getPosition();

    // Resolve HP: base + bind-path hpBonus (Fortify) + chimera hpBonus (Apex)
    const bindHpBonus = this.deps.resolver.getAdditiveEffect('bind', 'hpBonus');
    const chimeraHpBonus = this.deps.resolver.getAdditiveEffect('chimera', 'hpBonus');
    this._resolvedHp = this.config.baseHp + bindHpBonus + chimeraHpBonus;

    // Resolve damage: base + bind-path damageBonus (Resonant) + chimera damageBonus (Apex)
    const bindDmgBonus = this.deps.resolver.getAdditiveEffect('bind', 'damageBonus');
    const chimeraDmgBonus = this.deps.resolver.getAdditiveEffect('chimera', 'damageBonus');
    this._resolvedDamage = this.config.baseDamage + bindDmgBonus + chimeraDmgBonus;

    // Resolve intercept behavior (Fortify upgrade, 'bind' scope)
    const interceptEnabled = this.deps.resolver.hasEffect('bind', 'interceptEnabled');
    this._interceptEnabled = interceptEnabled;

    this._interceptCooldownMs = interceptEnabled
      ? (this.deps.resolver.getMinEffect('bind', 'interceptCooldownMs') ?? 2000)
      : 0;

    this._interceptMinHp = interceptEnabled
      ? (this.deps.resolver.getMaxEffect('bind', 'interceptMinHp') ?? 15)
      : 0;

    // Dual adapted behaviors — two randomly-selected trees, fresh roll per summon
    const selectedTrees = this.deps.progression.getRandomNonConjurationTrees(2);
    const behaviorA = resolveAdaptedBehavior(selectedTrees[0] ?? null);
    const behaviorB = resolveAdaptedBehavior(selectedTrees[1] ?? null);
    this._dualBehaviors = [behaviorA, behaviorB];

    // Synthesis upgrade: both behaviors fire simultaneously
    this._dualBehaviorSimultaneous = this.deps.resolver.hasEffect(
      'chimera', 'dualBehaviorSimultaneous',
    );

    // Apex overdrive — resolve damage multiplier, duration, and HP threshold
    this._overdriveMultiplier = this.deps.resolver.getMultiplicativeEffect(
      'chimera', 'lowHpOverdriveMultiplier',
    );
    this._overdriveDurationMs = this.deps.resolver.getAdditiveEffect(
      'chimera', 'lowHpOverdriveDurationMs',
    );
    this._overdriveHpThreshold = this.deps.resolver.getMaxEffect(
      'chimera', 'lowHpThreshold',
    ) ?? 0;
    // Reset overdrive state on fresh summon
    this._overdriveActive = false;
    this._overdriveCooldown = false;
    this._overdriveTimerRemaining = 0;

    const companionConfig: CompanionSummonConfig = {
      id: 'chimera_companion',
      archetype: 'companion',
      baseHp: this._resolvedHp,
      baseDamage: this._resolvedDamage,
      baseSpeed: this.config.baseSpeed,
      leashRadiusTiles: this.config.leashRadiusTiles,
      aggroWeight: this.config.baseAggroWeight,
      aiMode: 'tactical',
      attackRangeTiles: this.config.attackRangeTiles,
      attackRecoveryMs: this.config.attackRecoveryMs,
      stunTargetPriority: true,
      retreatHpThreshold: this.config.retreatHpThreshold,
      threatPositioning: true,
      ...(interceptEnabled && {
        interceptEnabled: true,
        interceptCooldownMs: this._interceptCooldownMs,
        interceptMinHp: this._interceptMinHp,
      }),
      dualBehaviors: this._dualBehaviors,
      dualBehaviorSimultaneous: this._dualBehaviorSimultaneous,
      ...(this._overdriveMultiplier > 0 && {
        overdriveMultiplier: this._overdriveMultiplier,
        overdriveDurationMs: this._overdriveDurationMs,
        overdriveHpThreshold: this._overdriveHpThreshold,
      }),
    };

    this._familiarId = this.deps.summons.spawn(companionConfig, pos.x, pos.y);
    this._familiarAlive = true;
    this._spawnsCount++;

    this._cooldownRemaining = this.config.cooldownMs;
    return true;
  }

  deactivate(): void {
    // Instant skill — no toggle state
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }

    // Tick overdrive timer — when it expires, deactivate and enter cooldown
    if (this._overdriveActive && this._overdriveTimerRemaining > 0) {
      this._overdriveTimerRemaining = Math.max(0, this._overdriveTimerRemaining - delta);
      if (this._overdriveTimerRemaining <= 0) {
        this._overdriveActive = false;
        this._overdriveCooldown = true;
      }
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
    this._familiarAlive = false;
    this._familiarId = '';
  }
}
